/**
 * Onboarding help content for the AI agent pages — a one-line tip, an improved
 * placeholder, and click-to-fill example prompts. Grounded in the demo sample
 * corpus so the examples actually return results.
 *
 * Shipped in English + Japanese (the flagship market); other UI locales fall
 * back to English via `pickLang`. Kept here (not in the 14 locale JSONs) so the
 * content stays in one place and doesn't bloat the i18n parity gate.
 *
 * For Audit, `groups` is keyed by compliance scope (jsox/sox/gdpr/appi/general)
 * so the chips change with the selected scope; every other page uses one
 * `default` group. Compare has no text input, so it carries a tip only.
 */
export interface LangStr {
  en: string;
  ja: string;
}

export interface HelpExampleGroup {
  key: string;
  examples: LangStr[];
}

export interface AgentHelpEntry {
  tip: LangStr;
  /** Input placeholder + example groups only apply to agent-input pages. */
  placeholder?: LangStr;
  groups?: HelpExampleGroup[];
}

export function pickLang(s: LangStr | undefined, lang: string | undefined): string {
  if (!s) return '';
  return (lang ?? 'en').split('-')[0] === 'ja' ? s.ja : s.en;
}

export function helpFor(page: string): AgentHelpEntry | undefined {
  return AGENT_HELP[page];
}

export function examplesFor(page: string, groupKey = 'default'): LangStr[] {
  const entry = AGENT_HELP[page];
  if (!entry) return [];
  return entry.groups?.find((g) => g.key === groupKey)?.examples ?? [];
}

/**
 * Common audiences for Compose / Brief. Quick-pick suggestions that fill the
 * free-text Audience field — not an exhaustive list, since audiences are
 * open-ended (a specific team, person, customer, or keigo target).
 */
export const AUDIENCE_PRESETS: LangStr[] = [
  { en: 'Executive team', ja: '経営陣' },
  { en: 'Board of directors', ja: '取締役会' },
  { en: 'Steering committee', ja: 'ステアリングコミッティ' },
  { en: 'Department managers', ja: '部門長' },
  { en: 'New hires', ja: '新入社員' },
  { en: 'All staff', ja: '全社員' },
  { en: 'Customers', ja: '顧客' },
  { en: 'Legal & compliance', ja: '法務・コンプライアンス' },
  { en: 'External partners', ja: '取引先' },
];

export const AGENT_HELP: Record<string, AgentHelpEntry> = {
  research: {
    tip: {
      en: 'Ask a broad or multi-part question. The agent plans sub-questions, gathers evidence across your whole corpus, and writes one structured, cited report.',
      ja: '広範または複数論点の質問をどうぞ。エージェントがサブ質問を計画し、コーパス全体から根拠を集め、構造化された出典付きレポートを作成します。',
    },
    placeholder: {
      en: 'e.g. How do our security, GDPR, and SOX controls compare, and where are the gaps?',
      ja: '例:当社のセキュリティ・GDPR・SOXの統制はどう比較でき、どこにギャップがあるか?',
    },
    groups: [
      {
        key: 'default',
        examples: [
          { en: 'Summarise our entire compliance posture across GDPR, APPI and J-SOX, with gaps.', ja: 'GDPR・APPI・J-SOXにわたる当社のコンプライアンス体制を、ギャップを含めて総括して。' },
          { en: 'What does onboarding to offboarding look like end-to-end, and which controls apply?', ja: '入社から退社までの流れと、適用される統制を端から端まで説明して。' },
          { en: 'Compare how we handle data retention, access reviews, and incident response.', ja: 'データ保持・アクセス棚卸し・インシデント対応の扱いを比較して。' },
        ],
      },
    ],
  },
  connectors: {
    tip: {
      en: 'Connect a source (Drive, Slack, Notion, kintone…) to pull its documents in. Authorize once, then Knowlez keeps it in sync — hit “Sync now” to pull immediately.',
      ja: 'ソース(Drive・Slack・Notion・kintone…)を接続すると文書を取り込めます。一度認証すれば自動同期。「今すぐ同期」で即時取り込みも可能です。',
    },
  },
  signals: {
    tip: {
      en: 'Subscribe to events on your knowledge plane — a new document indexed, an indexing failure — and get notified the moment they happen.',
      ja: '知識プレーンのイベント(新規文書のインデックス完了・失敗など)を購読すると、発生した瞬間に通知されます。',
    },
  },
  workflows: {
    tip: {
      en: 'Chain agent steps into a repeatable pipeline written in YAML — run it on demand or on a schedule. Steps can require human approval before continuing.',
      ja: 'エージェントのステップをYAMLで連結し、繰り返し実行できるパイプラインに。オンデマンドまたはスケジュール実行でき、途中に承認ゲートを挟めます。',
    },
  },
  ask: {
    tip: {
      en: "Type a natural question about your company's policies, procedures, or documents — answers come with citations.",
      ja: 'ポリシー、手続き、文書についての質問をお気軽に。すべての回答に出典が付きます。',
    },
    placeholder: {
      en: 'What are the GDPR data-subject rights we need to support?',
      ja: 'GDPRのデータ主体権利で対応すべき内容は何か',
    },
    groups: [
      {
        key: 'default',
        examples: [
          { en: 'What are the GDPR data-subject rights we must support?', ja: 'GDPRで対応が必要なデータ主体権利は何か' },
          { en: 'How do we handle financial-system access in SOX audits?', ja: 'SOX監査における財務システムアクセスの管理方法は' },
          { en: 'When should new hires be in the office during onboarding?', ja: 'オンボーディング期間中、新入社員はいつオフィス出社が必要か' },
          { en: 'What are the severity levels for production incidents?', ja: '本番環境のインシデント重大度レベルの定義は' },
          { en: 'Which vendors handle Confidential data and when?', ja: 'Confidential データを取り扱うベンダーはどこか、いつまでか' },
          { en: 'What approvals are needed for multi-year customer contracts?', ja: '複数年の顧客契約に必要な承認プロセスは' },
        ],
      },
    ],
  },
  search: {
    tip: {
      en: 'Type keywords from any document to find relevant passages — try a topic, policy term, or vendor name.',
      ja: 'ドキュメント内の任意のキーワードで関連セクションを検索できます。トピック、ポリシー用語、またはベンダー名から試してください。',
    },
    placeholder: {
      en: 'SCIM provisioning on-call page',
      ja: 'OpenRouter LLM ゲートウェイコスト',
    },
    groups: [
      {
        key: 'default',
        examples: [
          { en: 'SCIM provisioning access request process', ja: '新入社員アカウント プロビジョニング' },
          { en: 'GDPR data subject rights erasure exceptions', ja: '個人データ削除 例外 コンプライアンス' },
          { en: 'SOX financial controls segregation of duties', ja: '財務統制 職務分離 監査' },
          { en: 'Incident response Sev-1 mitigation playbook', ja: 'インシデント対応 緊急 復旧手順' },
          { en: 'Approved vendors AI tools customer data policy', ja: '承認ベンダー OpenRouter GitHub Copilot' },
          { en: 'Hanko invoice recognition accuracy rate', ja: 'ハンコ印影認識 精度改善 96%' },
        ],
      },
    ],
  },
  compose: {
    tip: {
      en: 'Draft professional outbound communication grounded in your knowledge base — emails, FAQs, policies, or announcements.',
      ja: 'ナレッジベースに基づいた敬語での対外発表文案をドラフトしましょう。メール、FAQ、ポリシー、告知など。',
    },
    placeholder: {
      en: 'Draft an email explaining our remote work policy to a new hire in Tokyo',
      ja: '東京の新入社員向けにリモートワークポリシーを説明するメール文案',
    },
    groups: [
      {
        key: 'default',
        examples: [
          { en: 'Draft FAQ entry: can engineers use ChatGPT on Knowlez data?', ja: 'FAQ: エンジニアはKnowlezデータでChatGPTを使用できますか' },
          { en: 'Draft a GDPR data-subject-rights response letter', ja: 'GDPR対象者の権利行使要求に対応するメール文案を作成' },
          { en: 'Draft quarterly board memo on SOX control updates', ja: '四半期ごとのSOX内部統制環境更新レポート案' },
          { en: 'Draft new-hire welcome email: security + remote-work', ja: 'セキュリティとリモートワーク要件をカバーした新入社員向けウェルカムメール' },
          { en: 'Draft incident notice: LLM gateway outage impact and ETA', ja: 'インシデント発表：LLMゲートウェイ障害の影響と復旧予定時刻' },
          { en: 'Draft policy update: new AI-tool approval for customer data', ja: 'ポリシー更新案内：顧客データを扱う新しいAIツール承認要件' },
        ],
      },
    ],
  },
  brief: {
    tip: {
      en: 'Enter a topic to synthesize a focused briefing with key sections for your audience.',
      ja: 'トピックを入力して、読み手に向けた構成化されたブリーフを作成します。',
    },
    placeholder: {
      en: 'GDPR data subject rights and our response process',
      ja: 'GDPRデータ主体権利と当社の対応手順',
    },
    groups: [
      {
        key: 'default',
        examples: [
          { en: 'New hire onboarding: accounts, hardware, first-week setup', ja: '新入社員オンボーディング：アカウント、ハードウェア、初週のセットアップ' },
          { en: 'SOX financial controls and segregation of duties', ja: 'SOX財務統制と職務分離' },
          { en: 'GDPR data subject rights and our response process', ja: 'GDPRデータ主体権利と当社の対応手順' },
          { en: 'Q1 2026 board meeting: product strategy and hiring plan', ja: '2026年Q1取締役会：プロダクト戦略と採用計画' },
          { en: 'Incident response severity levels and playbooks', ja: 'インシデント対応の重大度レベルと対応手順' },
          { en: 'Acceptable use policy: AI tools and data handling', ja: 'アクセプタブルユースポリシー：AIツールとデータ取扱ルール' },
        ],
      },
    ],
  },
  audit: {
    tip: {
      en: 'Pick a compliance scope, then ask about controls, evidence, or policy fit for your audit question.',
      ja: 'コンプライアンススコープを選択し、監査質問に関する管理策、証拠、またはポリシー適合性について質問してください。',
    },
    placeholder: {
      en: 'What segregation-of-duty controls exist for financial system access?',
      ja: '財務システムへのアクセスに関して、どのような職務分離管理策が存在しますか。',
    },
    groups: [
      {
        key: 'jsox',
        examples: [
          { en: 'What is our process for approving high-value contracts?', ja: '高額契約の承認プロセスは何ですか。' },
          { en: 'Who can approve ringi-sho budget decisions?', ja: '稟議書の予算決定を承認する権限者は誰ですか。' },
          { en: 'How do we enforce IT access controls on production?', ja: '本番システムのIT アクセス管理をどのように実施していますか。' },
          { en: 'What is our quarterly access recertification process?', ja: '四半期ごとのアクセス再認定プロセスは何ですか。' },
          { en: 'Which vendor contracts require CEO sign-off?', ja: 'CEO 承認が必要なベンダー契約はどれですか。' },
        ],
      },
      {
        key: 'sox',
        examples: [
          { en: 'What is the threshold for CFO approval of journal entries?', ja: '手作業仕訳の CFO 承認閾値は何ですか。' },
          { en: 'What SoD applies to vendor master and AP invoices?', ja: 'ベンダーマスターと AP 請求書の間の職務分離は何ですか。' },
          { en: 'How are financial-system changes reviewed and deployed?', ja: '財務システム変更をどのように確認し、本番環境にデプロイしていますか。' },
          { en: 'What is our revenue recognition policy for multi-year deals?', ja: '大型の複数年契約に対する収益認識ポリシーは何ですか。' },
          { en: 'How are privileged-access logs retained and audited?', ja: '特権アクセスログをどのように保持し、監査していますか。' },
        ],
      },
      {
        key: 'gdpr',
        examples: [
          { en: 'What is our timeline for responding to access requests?', ja: 'データ主体のアクセス要求に対応する期間は何ですか。' },
          { en: 'How do we handle right-to-erasure requests (Art. 17)?', ja: '第 17 条の削除権要求にどのように対応していますか。' },
          { en: 'What data formats do we provide for portability requests?', ja: 'データ移行権要求にはどのような形式を提供していますか。' },
          { en: 'Which recipients must be notified of data-subject requests?', ja: 'データ主体要求について通知すべき個人情報の受取人は誰ですか。' },
          { en: 'How do we restrict processing when a subject objects?', ja: 'データ主体がプロファイリングに異議を唱えた場合、処理をどのように制限していますか。' },
        ],
      },
      {
        key: 'appi',
        examples: [
          { en: 'What is our legal basis for processing personal information?', ja: '顧客の個人情報を処理する法的根拠は何ですか。' },
          { en: 'How do we comply with cross-border transfer rules?', ja: '個人データの越境移転ルール遵守をどのように確認していますか。' },
          { en: 'What opt-out and consent mechanisms exist for marketing?', ja: 'マーケティングのためのオプトアウトと同意メカニズムは何ですか。' },
          { en: 'How do third-party vendors handle personal data we disclose?', ja: 'サードパーティベンダーに開示された個人データをどのように取り扱っていますか。' },
          { en: 'What is our retention policy for inactive accounts?', ja: '非アクティブユーザーアカウントのデータ保持ポリシーは何ですか。' },
        ],
      },
      {
        key: 'general',
        examples: [
          { en: 'What must employees report if they suspect a breach?', ja: 'セキュリティ違反の疑いがある場合、従業員は何を報告しなければなりませんか。' },
          { en: 'What is the severity matrix and escalation path?', ja: '本番インシデントの重大度マトリクスとエスカレーションパスは何ですか。' },
          { en: 'Which AI vendors are approved for customer data?', ja: '顧客データ処理が承認されている AI ベンダーはどれですか。' },
          { en: 'What email/chat retention policy applies to executives?', ja: '経営陣に適用されるメールおよびチャット保持ポリシーは何ですか。' },
          { en: 'How do we handle remote-access security for WFH?', ja: 'リモートワークポリシーとリモートアクセスセキュリティをどのように管理していますか。' },
        ],
      },
    ],
  },
  compare: {
    tip: {
      en: 'Pick two indexed documents to see their differences, similarities, and a summary.',
      ja: '索引されたドキュメントを2つ選んで、差分や共通点、概要を確認できます。',
    },
    placeholder: {
      en: 'Select Document A and Document B to compare (e.g., two policy versions)',
      ja: '比較するドキュメントAとBを選択してください（例：ポリシーの2つのバージョン）',
    },
    groups: [],
  },
};
